import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { useActivityData } from './UseActivityData';
import { Chatroom, Member, MemberProfile } from '../../models';
import { UserRole } from '../../models/userRole';
import { getMemberProfile } from '../../utils/api-calls/getMemberProfile';
import { putUserRole } from '../../utils/api-calls/putUserRole';
import Spinner from '../../share/Spinner';

function RoleDropdown({
  room,
  userId,
  currentRole,
}: {
  room: Chatroom;
  userId: string;
  currentRole: string;
}) {
  const [t] = useTranslation(['chatroom']);
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(currentRole);

  const updateRole = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    try {
      const newRole = event.target.value;
      setSelectedRole(newRole);
      putUserRole(newRole, room.id, userId).then(() => {
        // Refresh page to update member's role if successfully update
        window.location.reload();
      });
    } catch (e) {
      console.error(e);
      navigate('/', { replace: true });
    }
  };

  const roleOptions = [UserRole.ASSISTANT, UserRole.PARTNER, UserRole.CUSTOMER];

  // Only room owner can change role, other users should display the current role
  if (room.role !== UserRole.OWNER) {
    return (
      <span className="text-sm rounded-md ml-2 p-1 px-2 bg-indigo-100">
        {t(`user_role_${selectedRole}`, selectedRole)}
      </span>
    );
  }

  // Owner role should not be changed
  if (selectedRole === UserRole.OWNER) {
    return (
      <span className="text-sm rounded-md ml-2 p-1 px-2 bg-indigo-100">
        {t(`user_role_owner`, '組長')}
      </span>
    );
  }

  // Display member role in dropdown
  if (selectedRole === UserRole.MEMBER) {
    roleOptions.push(selectedRole);
  }

  return (
    <select
      value={selectedRole}
      onChange={updateRole}
      className="text-sm rounded-md border-none shadow-sm bg-indigo-100 focus:border-none focus:ring-0"
    >
      {roleOptions.map((role) => (
        <option className="text-sm" key={role} value={role}>
          {t(`user_role_${role}`, role)}
        </option>
      ))}
    </select>
  );
}

function MemberProfileCard({
  member,
  memberProfile,
}: {
  member: Member;
  memberProfile: MemberProfile;
}) {
  const [t] = useTranslation(['chatroom']);
  const { room } = useActivityData();
  const tags = memberProfile?.tags.slice(0, 3) || []; // Only show top 3 tags

  if (!room) {
    return <></>;
  }

  return (
    <div className="flex flex-col py-4 w-[350px] bg-white rounded-md">
      <div className="flex flex-row justify-center items-center mt-4 my-2 gap-4 rounded-full">
        <img
          alt="member profile"
          src={memberProfile.pictureUrl}
          className="z-10 aspect-square w-[80px] h-[80px] rounded-full object-cover"
        />
        <div className="flex flex-col gap-2">
          <div className="block text-xl px-4 font-bold text-ellipsis overflow-hidden">
            {member.name}
          </div>
          <div className="text-sm px-2 rounded-md">
            <RoleDropdown
              room={room}
              userId={member.id}
              currentRole={member.role}
            />
          </div>
        </div>
      </div>
      <div className="mt-4 px-2">
        <div className="gap-1 flex flex-col mx-auto">
          {memberProfile.recentInteractiveTime && (
            <div className="text-md text-ellipsis px-4 overflow-hidden">
              {t(
                'member_profile_modal_recent_interactive_time',
                '最近的互動時間： {{time}}',
                {
                  time: memberProfile.recentInteractiveTime
                    ? memberProfile.recentInteractiveTime
                    : t('member_profile_modal_no_data', '無資料'),
                }
              )}
            </div>
          )}
          {memberProfile.recentChatroom && (
            <div className="text-md text-ellipsis px-4 overflow-hidden">
              {t(
                'member_profile_modal_recent_joined_chatroom',
                '最近的聊天室： {{chatroom}}',
                {
                  chatroom: memberProfile.recentChatroom
                    ? memberProfile.recentChatroom
                    : t('member_profile_modal_no_data', '無資料'),
                }
              )}
            </div>
          )}
          <div className="text-md px-4 flex flex-row items-start">
            {t('member_profile_modal_recent_tags', '最近的標籤：')}
            {tags.length > 0 ? (
              <div className="items-center w-[200px]">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-block max-w-[200px] gap-1 text-sm text-ellipsis overflow-hidden whitespace-nowrap bg-gray-200 p-1 rounded-md mx-1 my-0.5"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            ) : (
              t('member_profile_modal_no_data', '無資料')
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MemberProfileModal({
  isOpen,
  member,
  onMemberProfileModalClose,
}: {
  isOpen: boolean;
  member: Member;
  onMemberProfileModalClose: () => void;
}) {
  const [memberProfile, setMemberProfile] = useState<MemberProfile>();
  const memberProfileCardClassName =
    'top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] absolute outline-none';
  const spinnerClassName = 'absolute outline-none';

  useEffect(() => {
    if (isOpen) {
      getMemberProfile(member.id)
        .then((res) => {
          setMemberProfile(res);
        })
        .catch((e) => {
          console.log(e);
          onMemberProfileModalClose();
        });
    } else {
      setMemberProfile(undefined);
    }
  }, [isOpen, member, onMemberProfileModalClose]);

  return (
    <ReactModal
      isOpen={isOpen}
      overlayClassName="z-[51] fixed bg-black/[.4] inset-0 backdrop-blur-sm"
      className={memberProfile ? memberProfileCardClassName : spinnerClassName}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      onRequestClose={onMemberProfileModalClose}
    >
      {memberProfile ? (
        <MemberProfileCard member={member} memberProfile={memberProfile} />
      ) : (
        <Spinner />
      )}
    </ReactModal>
  );
}
